import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'
import Cookies from 'js-cookie'
import { Dialog, Button, Intent, Spinner } from '@blueprintjs/core'

import { PREVIEW_CLASS_NAMES } from './constants'
import Model from './model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import AutoSave from '@client/utils/Autosave'
import {
  APP_CONFIG_GET_A_NEWS_LETTER_API,
  APP_CONFIG_USE_LOCAL_PREVIEW,
  APP_CONFIG_MOCK_PROXY_TARGET,
} from '@config/app.config'
import {
  ITEM_TYPE_MAIL,
  ITEM_TYPE_CONFIRMATION_MAIL,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_FORM,
  ITEM_TYPE_LANDING_PAGE,
  ITEM_TYPE_LANDING_PAGE_SUCCESS,
  ITEM_TYPE_SURVEY,
  ITEM_TYPE_SURVEY_THANK_YOU,
  WIZARD_TYPE_FORM,
  WIZARD_TYPE_PAGE,
  WIZARD_TYPE_SURVEY,
} from '@client/components/Editor/settings'

import './styles.scss'

@inject('editor', 'app')
@observer
class Preview extends Component {
  static propTypes = {
    editor: PropTypes.instanceOf(EditorModel),
    app: PropTypes.instanceOf(AppModel),
  }

  static defaultProps = {
    editor: EditorModelNULL,
    app: AppModelNULL,
  }

  componentDidMount() {
    window.addEventListener('message', this.handleFrameMessages, false)
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleFrameMessages)
  }

  handleFrameMessages = (event) => {
    const { editor } = this.props
    if (editor.form_type !== 'slide_in') {
      return
    }
    try {
      if (event.origin !== new URL(this.getPreviewUrl()).origin) {
        return
      }
    } catch {
      return
    }
    if (event && event.data && 'string' == typeof event.data) {
      const dataParts = event.data.split('|')
      if (dataParts.length > 1 && 'gan' == dataParts[0]) {
        if (
          dataParts[1] === 'accounts'
        ) {
          let options = dataParts[3] || {}
          if (!options.hash) {
            try {
              options = JSON.parse(options)
            } catch (e) {
              console.error(e)
            }
          }
          if (options.popup_width) {
            // +20 accounts for the extra space the slide-in form template renders beyond popup_width
            // (close button and wrapper padding), so the iframe is wide enough to avoid clipping.
            // Floor at 700px so we clear the form template's `@media (max-width: 660px)` rule —
            // anything between popupWidth and 660 lands the columns in a cramped 50/50 layout.
            this.setState({
              iframeWidth: Math.max(parseInt(options.popup_width) + 20, 700),
            })
          }
          if (options.height) {
            this.setState({ iframeHeight: options.height })
          }
        }
      }
    }
  }

  showDesktop = () => {
    Model.setPreviewMode(PREVIEW_CLASS_NAMES.DESKTOP)
  }

  showMobile = () => {
    Model.setPreviewMode(PREVIEW_CLASS_NAMES.MOBILE)
  }

  closeDialog = () => {
    Model.setShowPreview(false)
    Model.setPreviewMode(PREVIEW_CLASS_NAMES.DESKTOP)
    Model.setShowSpinner(true)
    AutoSave.bind(this.props.editor)
  }

  onFrameLoad = (event) => {
    AutoSave.unBind()
    Model.setShowSpinner(false)
  }

  getPreviewUrl = () => {
    const { editor, app } = this.props
    let path
    if (APP_CONFIG_USE_LOCAL_PREVIEW) {
      const jwt = Cookies.get('jwt')
      if (editor.kind === ITEM_TYPE_MAIL && !app.hasWizard) {
        path = `mails/preview/${editor.id}`
      }
      if (app.hasWizard && app.wizard.type === WIZARD_TYPE_FORM) {
        if (editor.kind === ITEM_TYPE_CONFIRMATION_MAIL) {
          path = `mockapi/forms/${
            editor.itemHash
          }/${`${ITEM_TYPE_MAIL}`.toLowerCase()}/preview`
        }
        if (editor.kind === ITEM_TYPE_PAGE) {
          path = `mockapi/forms/${
            editor.itemHash
          }/${`${ITEM_TYPE_PAGE}`.toLowerCase()}/preview`
        }
        if (editor.kind === ITEM_TYPE_FORM && !editor.subKind) {
          path = `mockapi/forms/${
            editor.itemHash
          }/${`${ITEM_TYPE_FORM}`.toLowerCase()}/preview/`
        }
        if (editor.kind === ITEM_TYPE_FORM && editor.subKind) {
          path = `mockapi/forms/${editor.itemHash}/form-success/preview/`
        }
      } else if (app.hasWizard && app.wizard.type === WIZARD_TYPE_PAGE) {
        if (editor.kind === ITEM_TYPE_CONFIRMATION_MAIL) {
          path = `mockapi/pages/${
            editor.itemHash
          }/${`${ITEM_TYPE_MAIL}`.toLowerCase()}/preview`
        }
        if (editor.kind === ITEM_TYPE_PAGE) {
          path = `mockapi/pages/${
            editor.itemHash
          }/${`${ITEM_TYPE_PAGE}`.toLowerCase()}/preview`
        }
        if (editor.kind === ITEM_TYPE_LANDING_PAGE) {
          path = `mockapi/pages/${
            editor.itemHash
          }/form/preview/`
        }
        if (editor.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS) {
          path = `mockapi/pages/${editor.itemHash}/form-success/preview/`
        }
      } else if (app.hasWizard && app.wizard.type === WIZARD_TYPE_SURVEY) {
        if (editor.kind === ITEM_TYPE_SURVEY) {
          path = `mockapi/surveys/${
            editor.itemHash
          }/form/preview/`
        }
        if (editor.kind === ITEM_TYPE_SURVEY_THANK_YOU) {
          path = `mockapi/surveys/${editor.itemHash}/thank-you-page/preview/`
        }
      }

      return `${APP_CONFIG_MOCK_PROXY_TARGET}/${path}?jwt=${jwt}`
    }
    if (app.hasWizard) {
      if (app.wizard.type === WIZARD_TYPE_FORM) {
        if (editor.kind === ITEM_TYPE_CONFIRMATION_MAIL) {
          path = `forms/${
            editor.itemHash
          }/${`${ITEM_TYPE_MAIL}`.toLowerCase()}/preview`
        }
        if (editor.kind === ITEM_TYPE_PAGE) {
          path = `forms/${
            editor.itemHash
          }/${`${ITEM_TYPE_PAGE}`.toLowerCase()}/preview`
        }
        if (editor.kind === ITEM_TYPE_FORM && !editor.subKind) {
          path = `forms/${
            editor.itemHash
          }/${`${ITEM_TYPE_FORM}`.toLowerCase()}/preview/`
        }
        if (editor.kind === ITEM_TYPE_FORM && editor.subKind) {
          path = `forms/${editor.itemHash}/form-success/preview/`
        }
      } else if (app.wizard.type === WIZARD_TYPE_PAGE) {
        if (editor.kind === ITEM_TYPE_CONFIRMATION_MAIL) {
          path = `pages/${
            editor.itemHash
          }/${`${ITEM_TYPE_MAIL}`.toLowerCase()}/preview`
        }
        if (editor.kind === ITEM_TYPE_PAGE) {
          path = `pages/${
            editor.itemHash
          }/${`${ITEM_TYPE_PAGE}`.toLowerCase()}/preview`
        }
        if (editor.kind === ITEM_TYPE_LANDING_PAGE) {
          path = `pages/${
            editor.itemHash
          }/form/preview/`
        }
        if (editor.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS) {
          path = `pages/${editor.itemHash}/form-success/preview/`
        }
      } else if (app.wizard.type === WIZARD_TYPE_SURVEY) {
        if (editor.kind === ITEM_TYPE_SURVEY) {
          path = `surveys/${
            editor.itemHash
          }/form/preview/`
        }
        if (editor.kind === ITEM_TYPE_SURVEY_THANK_YOU) {
          path = `surveys/${editor.itemHash}/thank-you-page/preview/`
        }
      }
      return `${APP_CONFIG_GET_A_NEWS_LETTER_API}/${path}`
    }
    return editor.previewUrl
  }

  render() {
    const {
      editor,
      app: { formatMessage },
    } = this.props
    const isDesktop =
      Model.previewMode === PREVIEW_CLASS_NAMES.DESKTOP ? 'bp6-active' : ''
    const isMobile =
      Model.previewMode === PREVIEW_CLASS_NAMES.MOBILE ? 'bp6-active' : ''

    const previewUrl = this.getPreviewUrl()

    return (
      <div>
        <Dialog
          className="preview-dialog"
          icon="eye-open"
          isOpen={Model.showPreview}
          onClose={this.closeDialog}
          title={formatMessage('preview.dialog.title')}
        >
          <div className="bp6-dialog-body">
            <div className="preview-toggle-btns text-center">
              <div className="bp6-button-group">
                <button
                  className={`bp6-button bp6-icon-desktop ${isDesktop}`}
                  tabIndex="0"
                  type="button"
                  onClick={this.showDesktop}
                >
                  {formatMessage('preview.tab.desktop.title')}
                </button>
                <button
                  className={`bp6-button bp6-icon-mobile-phone ${isMobile}`}
                  tabIndex="0"
                  type="button"
                  onClick={this.showMobile}
                >
                  {formatMessage('preview.tab.mobile.title')}
                </button>
              </div>
            </div>
            <div className={`preview-iframe-wrapper ${Model.previewMode}`}>
              <div
                className={`preview-iframe ${Model.previewMode}${editor.form_type === 'slide_in' && editor.kind === ITEM_TYPE_FORM ? ' preview-iframe--slide-in' : ''}`}
              >
                <If condition={!this.props.editor.isSaving}>
                  <Choose>
                    <When condition={editor.form_type === 'slide_in' && editor.kind === ITEM_TYPE_FORM}>
                      <div style={{ flex: 1 }} />
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <iframe
                          src={previewUrl}
                          scrolling="no"
                          onLoad={this.onFrameLoad}
                          style={{
                            flex: 'none',
                            width: !isMobile && this.state?.iframeWidth ? this.state.iframeWidth : '100%',
                            height: this.state?.iframeHeight || undefined,
                            border: 'none',
                          }}
                        />
                      </div>
                    </When>
                    <Otherwise>
                      <iframe
                        width="100%"
                        src={previewUrl}
                        onLoad={this.onFrameLoad}
                      />
                    </Otherwise>
                  </Choose>
                </If>
                <If condition={Model.showSpinner}>
                  <div className="spinner-wrapper-center">
                    <Spinner />
                  </div>
                </If>
              </div>
            </div>
          </div>
          <div className="bp6-dialog-footer">
            <div className="bp6-dialog-footer-actions">
              <a
                href={previewUrl}
                target="__blank"
                rel="noopener noreferrer"
                className="bp6-text-small"
              >
                {formatMessage('preview.previewInNewTab')}
              </a>
              <Button
                intent={Intent.SUCCESS}
                onClick={this.closeDialog}
                text={formatMessage('preview.btnDone')}
              />
            </div>
          </div>
        </Dialog>
      </div>
    )
  }
}

export default Preview
